import { type StorageAttachmentRecord } from 'hb-react/shared/hooks/useGlobalStorageV2/types';

// Common interfaces used by both formats
interface NewsCategory {
  id: number;
  name: string;
}

interface NewsLikes {
  count: number;
  likeableId: number;
}

interface NewsUpload {
  fileName: string;
  fileUrl: string;
  id: number;
}

interface NewsAttachedImage {
  defaultUrl: string;
  id: number;
  name: string;
}

interface NewsGroup {
  accessibleByCurrentAdmin: boolean;
  coverPictureSmall: Pick<StorageAttachmentRecord, 'previewUrl'>;
  id: number;
  name: string;
}

// Legacy cover picture structure (used in old format)
interface NewsLegacyCoverPicture {
  byteSize: number;
  compressedUrl: string;
  downloadUrl: string;
  fileName: string;
  fileUrl: string;
  filename: string;
  friendUrl: string;
  htmlUrl: string;
  iconUrl: string;
  id: number;
  mediumUrl: string;
  mimeType: string;
  miniUrl: string;
  name: string;
  originalUrl: string;
  previewUrl: string;
  size: number;
  thumbnailUrl: string;
  virusScanStatus: string;
  wideUrl: string;
}

// New cover picture structure (used in new format)
interface NewsCoverPicture {
  compressedUrl: string;
  mediumUrl: string;
  previewUrl: string;
  wideUrl: string;
}

// Related letters structure
export interface NewsRelatedLetter {
  categories: Array<NewsCategory>;
  content: string;
  feedableAt: string;
  id: number;
  newCoverPicture: NewsLegacyCoverPicture;
  pinned: boolean;
  publishedAt: string;
  subject: string;
}

export interface GetNewsByIdResponse {
  attachedImages: Array<NewsAttachedImage>;
  attachments: Array<StorageAttachmentRecord>;
  canonicalUrl: string;
  categories: Array<NewsCategory>;
  content: string;
  createdAt: string;
  externalUrl: string | null;
  feedableAt: string;
  id: number;
  likes: NewsLikes;
  newCoverPicture: NewsLegacyCoverPicture;
  ogUrl: string; // Alias for backwards compatibility
  parentId: number;
  parentType: string;
  pinned: boolean;
  publishedAt: string;
  relatedLetters: Array<NewsRelatedLetter>;
  seoDescription: string;
  seoImage: string;
  seoTitle: string;
  socialMediaList: Array<string>;
  subject: string;
  type: string;
  updatedAt: string;
  uploads: Array<NewsUpload>;
}
export interface GetNewsByUuidResponse {
  attachments: Array<StorageAttachmentRecord>;
  categories: Array<NewsCategory>;
  content: string;
  coverPicture: NewsCoverPicture;
  emailContent: string;
  groupCreator: NewsGroup | null;
  groups: Array<NewsGroup>;
  id: string;
  likes: NewsLikes;
  ogUrl: string;
  pinnedToFeed: boolean;
  pinnedToIndexPage: boolean;
  public: boolean;
  publicationTimezone: string | null;
  publishedAt: string;
  rssUrl: string | null;
  sanitizedContent: string;
  seoCanonicalUrl: string;
  seoDescription: string;
  seoTitle: string;
  // Alias for backwards compatibility
  shareUrl: string;
  socialMediaList: Array<string>;
  subject: string;
  type: string;
  visibleOnFeed: boolean;
  visibleOnIndexPage: boolean;
}

export interface PublishedNewsData {
  attachments: Array<StorageAttachmentRecord>;
  categories: Array<NewsCategory>;
  content: string;
  coverPicture: NewsCoverPicture;
  id: string;
  likes: NewsLikes;
  ogUrl: string;
  publishedAt: string;
  relatedLetters: Array<NewsRelatedLetter>;
  rssUrl?: string;
  seoCanonicalUrl: string;
  seoDescription: string;
  seoTitle: string;
  // Alias for backwards compatibility
  shareUrl: string;
  socialMediaList: Array<string>;
  subject: string;
}

export interface WrappedGetNewsByIdResponse {
  postCommunication: GetNewsByIdResponse;
}

// Union type for API responses
export type GetNewsResponse =
  | GetNewsByUuidResponse // Direct new format (by UUID)
  | WrappedGetNewsByIdResponse; // Wrapped old format (by ID)

// Interface for the related news API response structure (camelCase after fetch transformation)
export interface RelatedNewsItem {
  content: string;
  coverPicture: {
    compressedUrl: string;
    mediumUrl: string;
    previewUrl: string;
    wideUrl: string;
  };
  id: string;
  publishedAt: string;
  seoDescription: string | null;
  seoTitle: string | null;
  subject: string;
}

// Normalized interface for what NewsRelatedRow component expects
export interface RelatedNewsNormalized {
  content: string;
  coverPicture?: {
    mediumUrl?: string;
    previewUrl?: string;
  };
  id?: string;
  publishedAt: string;
  seoDescription?: string;
  seoTitle?: string;
  subject: string;
}

// Type guards to distinguish between formats
export const isWrappedGetNewsByIdResponse = (
  news: GetNewsResponse,
): news is WrappedGetNewsByIdResponse => 'postCommunication' in news;
